import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Chip, Avatar,
  Divider, Button, CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';

export default function SubcontractorCVPreview() {
  const navigate = useNavigate();
  const { subId } = useParams();
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, 'subcontractors', subId));
      if (snap.exists()) setProfile(snap.data());

      // Hämta Tikabs företagsprofil för logotyp
      const companiesSnap = await getDoc(doc(db, 'companies', 'main'));
      if (companiesSnap.exists()) {
        setCompany(companiesSnap.data());
      } else {
        // Fallback: ta första companies-dokumentet
        const all = await getDocs(collection(db, 'companies'));
        if (!all.empty) setCompany(all.docs[0].data());
      }
      setLoading(false);
    }
    load();
  }, [subId]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );
  if (!profile) return (
    <Box sx={{ p: 4 }}>
      <Typography>Ingen profil hittad.</Typography>
    </Box>
  );

  return (
    <Box>
      {/* Navigationsbar – döljs vid utskrift */}
      <Box className="no-print" sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button color="inherit" startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/subcontractors/${subId}`)}>
              Tillbaka
            </Button>
            <Typography variant="h5" fontWeight="bold">Underkonsultprofil</Typography>
            <Chip label="Underkonsult" size="small"
              sx={{ bgcolor: '#FF6B00', color: 'white', fontSize: 11 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<EditIcon />}
              sx={{ borderColor: 'white', color: 'white' }}
              onClick={() => navigate(`/subcontractors/${subId}`)}>
              Redigera
            </Button>
            <Button variant="contained" startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}>
              Skriv ut / Spara PDF
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}
        className="cv-background">
        <Container maxWidth="md">
          <Paper elevation={3} id="cv-content"
            sx={{ p: 5, borderRadius: 2, bgcolor: '#ffffff', color: '#111' }}>

            {/* SIDHUVUD */}
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              mb: 4, pb: 2, borderBottom: '3px solid #1a1a1a'
            }}>
              <Box>
                {company?.logo ? (
                  <Box component="img" src={company.logo}
                    sx={{ height: 50, objectFit: 'contain' }} />
                ) : (
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    {company?.name || ''}
                  </Typography>
                )}
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="overline" sx={{ color: '#888', letterSpacing: 3 }}>
                  UNDERKONSULTPROFIL
                </Typography>
                {profile.personal?.company && (
                  <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}
                    display="block">
                    via {profile.personal.company}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: '#888' }} display="block">
                  {new Date().toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' })}
                </Typography>
              </Box>
            </Box>

            {/* PERSONUPPGIFTER */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4, alignItems: 'flex-start' }}>
              <Avatar src={profile.personal?.photo}
                sx={{ width: 100, height: 100, fontSize: 40, flexShrink: 0 }}>
                {!profile.personal?.photo && (profile.personal?.name?.charAt(0) || '?')}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#111' }}>
                  {profile.personal?.name}
                </Typography>
                <Typography variant="h6" sx={{ color: '#444' }} gutterBottom>
                  {profile.personal?.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {profile.personal?.company && (
                    <Typography variant="body2" sx={{ color: '#333', fontWeight: 'bold' }}>
                      🏢 {profile.personal.company}
                    </Typography>
                  )}
                  {profile.personal?.location && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      📍 {profile.personal.location}
                    </Typography>
                  )}
                  {profile.personal?.email && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      ✉ {profile.personal.email}
                    </Typography>
                  )}
                  {profile.personal?.phone && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      ☎ {profile.personal.phone}
                    </Typography>
                  )}
                  {profile.personal?.linkedin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                        width="16" height="16" fill="#0A66C2">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        {profile.personal.linkedin}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#ddd' }} />

            {/* INGRESS */}
            {profile.ingress && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1"
                  sx={{ lineHeight: 1.8, fontStyle: 'italic', color: '#222' }}>
                  {profile.ingress}
                </Typography>
              </Box>
            )}

            {/* KOMPETENSER */}
            {profile.competencies?.length > 0 && (
              <CVSection title="Kompetenser">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {profile.competencies.map((c, i) => (
                    <Chip key={i} label={c} size="small" variant="outlined"
                      sx={{ color: '#222', borderColor: '#999', bgcolor: 'transparent' }} />
                  ))}
                </Box>
              </CVSection>
            )}

            {/* UPPDRAG */}
            {profile.assignments?.length > 0 && (
              <CVSection title="Uppdrag">
                {profile.assignments.map((a, i) => (
                  <Box key={i} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography fontWeight="bold" sx={{ color: '#111' }}>{a.name}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#555' }}>
                      {[a.period, a.client, a.role].filter(Boolean).join(' | ')}
                    </Typography>
                    {a.projectDescription && (
                      <Typography variant="body2" sx={{ color: '#444', mt: 1 }}>
                        {a.projectDescription}
                      </Typography>
                    )}
                    {a.personalDescription && (
                      <Box sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid #333' }}>
                        <Typography variant="caption" fontWeight="bold" sx={{ color: '#222' }}>
                          Roll och insats
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#333', mt: 0.5 }}>
                          {a.personalDescription}
                        </Typography>
                      </Box>
                    )}
                    {i < profile.assignments.length - 1 && (
                      <Divider sx={{ mt: 2, borderColor: '#eee' }} />
                    )}
                  </Box>
                ))}
              </CVSection>
            )}

            {/* UTBILDNING */}
            {profile.education?.length > 0 && (
              <CVSection title="Utbildning">
                {profile.education.map((e, i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography fontWeight="bold" sx={{ color: '#111' }}>{e.degree}</Typography>
                      <Typography variant="caption" sx={{ color: '#555' }}>{e.year}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#444' }}>{e.school}</Typography>
                    {i < profile.education.length - 1 && (
                      <Divider sx={{ mt: 1.5, borderColor: '#eee' }} />
                    )}
                  </Box>
                ))}
              </CVSection>
            )}

            {/* VERKTYG */}
            {(profile.tools?.expert?.length > 0 || profile.tools?.good?.length > 0) && (
              <CVSection title="Verktyg & programvaror">
                {profile.tools?.expert?.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                      Mycket goda kunskaper
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {[...profile.tools.expert].sort((a, b) => a.localeCompare(b, 'sv')).map((t, i) => (
                        <Chip key={i} label={t} size="small" variant="outlined"
                          sx={{ color: '#111', borderColor: '#333',
                            fontWeight: 'bold', bgcolor: 'transparent' }} />
                      ))}
                    </Box>
                  </Box>
                )}
                {profile.tools?.good?.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: '#333' }}>
                      Goda kunskaper
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {[...profile.tools.good].sort((a, b) => a.localeCompare(b, 'sv')).map((t, i) => (
                        <Chip key={i} label={t} size="small" variant="outlined"
                          sx={{ color: '#444', borderColor: '#666', bgcolor: 'transparent' }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </CVSection>
            )}

            {/* STANDARDER */}
            {profile.standards?.length > 0 && (
              <CVSection title="Standarder & regelverk">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {profile.standards.map((s, i) => (
                    <Chip key={i} label={s} size="small" variant="outlined"
                      sx={{ color: '#222', borderColor: '#888', bgcolor: 'transparent' }} />
                  ))}
                </Box>
              </CVSection>
            )}

            {/* BRANSCHER */}
            {profile.industries?.length > 0 && (
              <CVSection title="Branscherfarenhet">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {profile.industries.map((ind, i) => (
                    <Chip key={i} label={ind} size="small" variant="outlined"
                      sx={{ color: '#222', borderColor: '#888', bgcolor: 'transparent' }} />
                  ))}
                </Box>
              </CVSection>
            )}

          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

function CVSection({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline"
        sx={{ fontWeight: 'bold', color: '#1a1a1a', letterSpacing: 2, fontSize: 12 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 2, borderColor: '#333' }} />
      {children}
    </Box>
  );
}
